import React, { useEffect, useRef, useState } from "react";

const PasswordConfirmDialog = ({ open, onConfirm, onCancel }) => {
  const dialogRef = useRef(null);
  const submittingRef = useRef(false);

  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;

    if (open) {
      setPassword("");
      setError("");

      if (!dialog.open) dialog.showModal();
    } else if (dialog.open) {
      dialog.close();
      setPassword("");
    }

    return () => {
      if (dialog.open) dialog.close();
    };
  }, [open]);

  const cancel = () => {
    if (!submittingRef.current) onCancel();
  };

  const submit = async (event) => {
    event.preventDefault();

    if (submittingRef.current) return;

    if (!password.length) {
      setError("Enter your password to continue.");
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setError("");

    try {
      // Must reject if password verification or deletion fails.
      await onConfirm(password);
      setPassword("");
    } catch (err) {
      setPassword("");
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to delete. Please try again.",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="delete-password-title"
      aria-describedby="delete-password-description"
      onClick={(event) => event.stopPropagation()}
      onCancel={(event) => {
        event.preventDefault();
        cancel();
      }}
    >
      <form onSubmit={submit} aria-busy={submitting}>
        <h3 id="delete-password-title">Delete this record?</h3>

        <p id="delete-password-description">
          Enter your account password to confirm. This action cannot be undone.
        </p>

        <label>
          Password
          <input
            type="password"
            name="currentPassword"
            autoComplete="current-password"
            autoFocus
            required
            value={password}
            disabled={submitting}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "delete-password-error" : undefined}
            onChange={(event) => {
              setPassword(event.target.value);
              setError("");
            }}
          />
        </label>

        {error && (
          <p id="delete-password-error" role="alert">
            {error}
          </p>
        )}

        <div>
          <button type="button" disabled={submitting} onClick={cancel}>
            Cancel
          </button>

          <button type="submit" disabled={submitting || !password.length}>
            {submitting ? "Verifying and deleting…" : "Delete"}
          </button>
        </div>
      </form>
    </dialog>
  );
};

export default PasswordConfirmDialog;
